import React, { useEffect, useState } from 'react';
import { useAutomationSettings } from '../../hooks/useAutomations';
import { useToast } from '../../components/Toast';

const AutomationSettings = () => {
    const { settings, loading, save } = useAutomationSettings();
    const [draft, setDraft] = useState(null);
    const { showSuccess, showError } = useToast();

    const CHANNELS = [
        { key: 'whatsapp', label: 'WhatsApp', group: 'Messaging' },
        { key: 'sms', label: 'SMS', group: 'Messaging' },
        { key: 'email', label: 'Email', group: 'Messaging' },
        { key: 'telegram', label: 'Telegram', group: 'Messaging' },
        { key: 'instagram_dm', label: 'Instagram DM', group: 'Meta Social' },
        { key: 'facebook_messenger', label: 'Facebook Messenger', group: 'Meta Social' },
        { key: 'instagram_comment_reply', label: 'IG Comment Reply', group: 'Social Replies' },
        { key: 'facebook_comment_reply', label: 'FB Comment Reply', group: 'Social Replies' },
        { key: 'linkedin_message', label: 'LinkedIn (manual)', group: 'Other' },
        { key: 'webhook', label: 'Webhook', group: 'Other' },
    ];

    useEffect(() => {
        if (settings) {
            setDraft({
                ...settings,
                quietHours: settings.quietHours || { enabled: true, start: '21:00', end: '08:00' },
                optOutTags: settings.optOutTags || [],
                stopOnStatuses: settings.stopOnStatuses || [],
                defaultSender: settings.defaultSender || {},
                enabledChannels: settings.enabledChannels || CHANNELS.map((channel) => channel.key),
                perChannelApproval: settings.perChannelApproval || {},
            });
        }
    }, [settings]);

    if (loading || !draft) {
        return (
            <div className="crm-card crm-card-hover">
                <div className="crm-card-body">
                    <div className="crm-loading"><div className="crm-spinner" /></div>
                </div>
            </div>
        );
    }

    const handleSave = async () => {
        try {
            await save(draft);
            showSuccess('Settings saved');
        } catch (err) {
            showError(err.message || 'Failed to save settings');
        }
    };

    const updateTagList = (value) => (
        value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
    );

    return (
        <div className="crm-card crm-card-hover">
            <div className="crm-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 className="crm-card-title">Automation Settings</h2>
                <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={handleSave}>
                    Save
                </button>
            </div>
            <div className="crm-card-body" style={{ display: 'grid', gap: '20px' }}>
                <div className="crm-form-grid">
                    <div className="crm-form-group">
                        <label className="crm-label">Require approval before sending</label>
                        <select
                            className="crm-select"
                            value={draft.requireApproval ? 'true' : 'false'}
                            onChange={(e) => setDraft({ ...draft, requireApproval: e.target.value === 'true' })}
                        >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Max messages per lead per day</label>
                        <input
                            className="crm-input"
                            type="number"
                            value={draft.maxMessagesPerLeadPerDay || 0}
                            onChange={(e) => setDraft({ ...draft, maxMessagesPerLeadPerDay: Number(e.target.value || 0) })}
                        />
                    </div>
                </div>

                <div className="crm-card crm-card-subtle">
                    <div className="crm-card-header">
                        <h3 className="crm-card-title">Quiet Hours</h3>
                    </div>
                    <div className="crm-card-body">
                        <div className="crm-form-grid">
                            <div className="crm-form-group">
                                <label className="crm-label">Enabled</label>
                                <select
                                    className="crm-select"
                                    value={draft.quietHours?.enabled ? 'true' : 'false'}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        quietHours: { ...draft.quietHours, enabled: e.target.value === 'true' },
                                    })}
                                >
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Start</label>
                                <input
                                    className="crm-input"
                                    value={draft.quietHours?.start || '21:00'}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        quietHours: { ...draft.quietHours, start: e.target.value },
                                    })}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">End</label>
                                <input
                                    className="crm-input"
                                    value={draft.quietHours?.end || '08:00'}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        quietHours: { ...draft.quietHours, end: e.target.value },
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="crm-form-grid">
                    <div className="crm-form-group">
                        <label className="crm-label">Stop automation if status is</label>
                        <input
                            className="crm-input"
                            placeholder="converted, lost"
                            value={draft.stopOnStatuses?.join(', ') || ''}
                            onChange={(e) => setDraft({
                                ...draft,
                                stopOnStatuses: updateTagList(e.target.value),
                            })}
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Stop when lead replies</label>
                        <select
                            className="crm-select"
                            value={draft.stopOnReply ? 'true' : 'false'}
                            onChange={(e) => setDraft({ ...draft, stopOnReply: e.target.value === 'true' })}
                        >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Opt-out tags</label>
                        <input
                            className="crm-input"
                            placeholder="DNC, DoNotContact"
                            value={draft.optOutTags?.join(', ') || ''}
                            onChange={(e) => setDraft({
                                ...draft,
                                optOutTags: updateTagList(e.target.value),
                            })}
                        />
                    </div>
                </div>

                <div className="crm-card crm-card-subtle">
                    <div className="crm-card-header">
                        <h3 className="crm-card-title">Default Sender</h3>
                    </div>
                    <div className="crm-card-body">
                        <div className="crm-form-grid">
                            <div className="crm-form-group">
                                <label className="crm-label">Name</label>
                                <input
                                    className="crm-input"
                                    value={draft.defaultSender?.name || ''}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        defaultSender: { ...draft.defaultSender, name: e.target.value },
                                    })}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Email</label>
                                <input
                                    className="crm-input"
                                    value={draft.defaultSender?.email || ''}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        defaultSender: { ...draft.defaultSender, email: e.target.value },
                                    })}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Phone</label>
                                <input
                                    className="crm-input"
                                    value={draft.defaultSender?.phone || ''}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        defaultSender: { ...draft.defaultSender, phone: e.target.value },
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="crm-card crm-card-subtle">
                    <div className="crm-card-header">
                        <h3 className="crm-card-title">Enabled Channels</h3>
                    </div>
                    <div className="crm-card-body" style={{ display: 'grid', gap: '12px' }}>
                        {Array.from(new Set(CHANNELS.map((channel) => channel.group))).map((group) => (
                            <div key={group}>
                                <div className="crm-muted" style={{ marginBottom: '6px' }}>{group}</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                    {CHANNELS.filter((channel) => channel.group === group).map((channel) => {
                                        const enabled = draft.enabledChannels?.includes(channel.key);
                                        return (
                                            <label key={channel.key} className="crm-inline-check">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(enabled)}
                                                    onChange={(e) => {
                                                        const next = e.target.checked
                                                            ? Array.from(new Set([...(draft.enabledChannels || []), channel.key]))
                                                            : (draft.enabledChannels || []).filter((item) => item !== channel.key);
                                                        setDraft({ ...draft, enabledChannels: next });
                                                    }}
                                                />
                                                <span>{channel.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="crm-card crm-card-subtle">
                    <div className="crm-card-header">
                        <h3 className="crm-card-title">Approval by Channel</h3>
                    </div>
                    <div className="crm-card-body" style={{ display: 'grid', gap: '8px' }}>
                        {CHANNELS.map((channel) => (
                            <div key={channel.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>{channel.label}</div>
                                <select
                                    className="crm-select crm-select-sm"
                                    value={draft.perChannelApproval?.[channel.key] ? 'true' : 'false'}
                                    onChange={(e) => setDraft({
                                        ...draft,
                                        perChannelApproval: {
                                            ...(draft.perChannelApproval || {}),
                                            [channel.key]: e.target.value === 'true',
                                        },
                                    })}
                                >
                                    <option value="true">Require approval</option>
                                    <option value="false">Auto send</option>
                                </select>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AutomationSettings;
